import { Link } from "react-router-dom";
import { Shield, Lock, Eye, EyeOff, KeyRound, MailCheck, Info } from "lucide-react";

const Row = ({ icon: Icon, title, children }) => (
  <div className="flex gap-4 items-start">
    <div className="mt-1 shrink-0 w-9 h-9 rounded-full bg-sage-100 text-sage-700 flex items-center justify-center">
      <Icon size={18} />
    </div>
    <div>
      <h3 className="font-heading font-semibold text-ink-900">{title}</h3>
      <p className="text-ink-700 text-sm leading-relaxed mt-1">{children}</p>
    </div>
  </div>
);

export default function PrivacySecurity() {
  return (
    <div className="min-h-screen bg-ivory">
      <div className="max-w-2xl mx-auto px-5 py-10 space-y-8">
        <div>
          <div className="label-overline">Trust</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mt-2 text-ink-900">
            Privacy &amp; Security
          </h1>
          <p className="text-ink-500 text-sm mt-3 leading-relaxed">
            A plain-English summary of how AMDS handles your diary, your data, and your
            trust. For the formal legal versions see the
            {" "}<Link to="/privacy-policy" className="text-sage-600 underline">Privacy Policy</Link>{" "}
            and{" "}<Link to="/terms" className="text-sage-600 underline">Terms</Link>.
          </p>
        </div>

        <div className="card p-6 space-y-6">
          <Row icon={EyeOff} title="Your diary contents are private">
            The full contents of your diary — event titles, venues, notes, times of day — are
            visible only to you when signed in. Nobody else sees the inside of your diary.
          </Row>

          <Row icon={Eye} title="Only your availability is shared">
            When a booker searches for a musician on a given date, AMDS reveals only whether
            you are available, tentative, or unavailable for the relevant time band.
            <strong> Event titles, venues and notes are never exposed.</strong>
          </Row>

          <Row icon={Lock} title="Passwords are securely stored">
            Passwords are hashed with bcrypt before they touch the database. Even AMDS admins
            cannot see your password in plain text. If you forget it, you reset it via a
            time-limited email link.
          </Row>

          <Row icon={MailCheck} title="Email verification is required">
            New accounts must click a verification link before they can sign in. This keeps
            fake or hijacked email addresses off the platform. Verification emails are sent
            over Resend with SPF, DKIM and DMARC in place.
          </Row>

          <Row icon={Shield} title="Data is used only to operate AMDS">
            Your diary and profile data are used only to run AMDS features — search,
            availability, dep-finding, notifications. AMDS does not sell your data to third
            parties and does not use it to train external AI models.
          </Row>

          <Row icon={Info} title="AMDS is currently in beta">
            AMDS is early-stage software. You&apos;ll find rough edges, and I read every bug
            report and suggestion personally. If you spot something, please tell me — there
            is a Feedback link in the footer of every page.
          </Row>
        </div>

        <p className="text-center text-ink-500 text-xs">
          <Link to="/" className="hover:text-sage-600">Back to AMDS</Link>
        </p>
      </div>
    </div>
  );
}
